import { motion } from "framer-motion";
import {
  Bot,
  Search,
  PiggyBank,
  LayoutDashboard,
  Zap,
  Gift,
  ShieldCheck,
  RefreshCw,
  Star,
} from "lucide-react";
import { TRUST_POINTS, TESTIMONIALS } from "../../lib/landing-data";

const ICONS = { Bot, Search, PiggyBank, LayoutDashboard, Zap, Gift, ShieldCheck, RefreshCw };

export const WhyChoose = () => {
  return (
    <section id="why" className="relative py-20 sm:py-28 bg-gradient-to-b from-white to-purple-50/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider">
            Why EasyLister
          </span>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            Eight reasons sellers stay with us
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TRUST_POINTS.map((p, i) => {
            const Icon = ICONS[p.icon] || Zap;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
                whileHover={{ y: -4 }}
                data-testid={`trust-point-${i}`}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-purple-200 hover:shadow-lg hover:shadow-purple-600/5 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-heading text-base font-extrabold text-slate-900">{p.title}</div>
                <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{p.desc}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  return (
    <section id="testimonials" className="relative py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider">
            Loved by sellers
          </span>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            What sellers are saying
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`testimonial-card-${i}`}
              className="relative rounded-3xl border border-purple-100 bg-gradient-to-br from-white via-purple-50/30 to-white p-7 sm:p-8 hover:shadow-xl hover:shadow-purple-600/5 transition-all"
            >
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <blockquote className="text-base text-slate-800 leading-relaxed font-medium">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-11 h-11 rounded-full object-cover border-2 border-white shadow-md"
                />
                <div>
                  <div className="font-heading text-sm font-extrabold text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
